/**
 * A failed read on the resolutions page (ALL-5908).
 *
 * The page is two reads deep — the exceptions queue and the selected proposal's
 * resolutions — and both can be refused. The framing is the same for either: the
 * reason code the service sent, bounded, and its support reference. A 403 gets
 * the access-denied framing, because asking for the permission is the operator's
 * next step; anything else gets the not-available framing with a retry.
 *
 * Deliberately local rather than shared with the settings page: two pages that
 * happen to phrase a refusal the same way today do not need one module that
 * cannot be changed for either without changing both.
 */

import { Button } from "@/components/ui/button";
import { NotAvailableState, PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import type { ApiErrorInfo } from "@/lib/api";
import { isDeniedCode, RESOLUTIONS_COPY as COPY } from "./copy";

export function ReadRefusalNotice({
  error,
  onRetry,
  subject = "the proposals",
}: {
  error: ApiErrorInfo;
  onRetry: () => void;
  subject?: string;
}) {
  const known = COPY.refusals[error.reasonCode];

  if (isDeniedCode(error.reasonCode)) {
    return (
      <div data-testid="read-refusal">
        <PermissionDeniedState
          title="Access required"
          description={
            known ?? `You don't have access to ${subject}. Ask an organization admin to grant it.`
          }
          details={[
            { k: "Reason code", v: error.reasonCode },
            ...(error.capability ? [{ k: "Needs", v: error.capability }] : []),
          ]}
          action={
            <Button variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          }
          footnote={COPY.readonlyHint}
        />
        <SupportDetails reference={error.supportReference} className="mt-3" />
      </div>
    );
  }

  return (
    <div data-testid="read-refusal">
      <NotAvailableState
        title="Resolutions could not be read"
        description={known ?? `The service refused a read of ${subject}.`}
        reason={`Reason code: ${error.reasonCode}`}
      />
      <SupportDetails reference={error.supportReference} capability={error.capability} className="mt-3" />
      <div className="mt-3 flex justify-center">
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}
